import type { ScreenerColumn, ScreenerSubField } from "epsm-api-types/models";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWidgetView } from "@/context/WidgetViewContext";
import { isEnabledScorer } from "@/lib/utils/features";
import { flattenSubfieldsToDropdownOptions } from "@/lib/utils/flattenSubfieldsToDropdownOptions";
import { KEY } from "@/shared/constants/keyboard";
import sprites from "/src/shared/assets/sprite.svg";
import { scorerPrefix } from "../widgets/Screener/default";
import { Dropdown } from "../widgets/Screener/ScreenerFilters/Filters/ui/Dropdown";
import { ScreenerColumnService } from "../widgets/Screener/services/ScreenerColumnService";
import { getFirstSubfield } from "./columns";

type ColumnSetProps = {
	baseId: string;
	isScorerOpened?: boolean;
};

export const ColumnSet: React.FC<ColumnSetProps> = ({
	baseId,
	isScorerOpened,
}) => {
	const {
		visibleColumns,
		setVisibleColumns,
		selectedColumn,
		setSelectedColumn,
		setIsRelative,
		setScorerColumns,
		scorerColumns,
	} = useWidgetView();

	const parentRef = useRef<HTMLDivElement>(null);

	const [allSubFields, setAllSubFields] = useState<
		Record<string, ScreenerSubField[]>
	>({});

	const isScorerEnabled = isEnabledScorer();

	useEffect(() => {
		ScreenerColumnService.create()
			.getData()
			.then((columns) => {
				const subfieldsMap: Record<string, ScreenerSubField[]> = {};
				if (columns?.data) {
					for (const column of columns.data) {
						if (column.subFields && column.subFields.length > 0) {
							subfieldsMap[column.name] = column.subFields;
						}
					}
					setAllSubFields(subfieldsMap);
				}
			});
	}, []);

	const updateVisibleColumnsWithSubFields = (
		columnName: string,
		selectedSubfields: string[],
	) => {
		setVisibleColumns((prevColumns) => {
			const updatedColumns = prevColumns[baseId]?.map((col) => {
				if (col.name !== columnName) {
					return col;
				}

				const subfields = allSubFields[columnName];

				if (!subfields.length) {
					return col;
				}

				const processSubfields = (
					subFields: ScreenerSubField[],
					parentPath = "",
				): ScreenerSubField[] => {
					return subFields
						.map((subfield) => {
							const fullPath = parentPath
								? `${parentPath}.${subfield.name}`
								: subfield.name;

							const filteredSubFields = subfield.subFields?.length
								? processSubfields(subfield.subFields, fullPath)
								: [];

							if (
								selectedSubfields.includes(fullPath) ||
								filteredSubFields.length
							) {
								return {
									...subfield,
									...(filteredSubFields.length && {
										subFields: filteredSubFields,
									}),
								};
							}

							return null;
						})
						.filter((subfield) => subfield !== null);
				};

				const updatedSubfields = processSubfields(subfields);

				return {
					...col,
					subFields: updatedSubfields,
				};
			});

			return {
				...prevColumns,
				[baseId]: updatedColumns,
			};
		});
	};

	const removeVisibleColumn = (columnName: string) => {
		setVisibleColumns((prevColumns) => ({
			...prevColumns,
			[baseId]: prevColumns[baseId].filter((col) => col.name !== columnName),
		}));

		const updatedSelectedColumn = { ...selectedColumn };

		if (updatedSelectedColumn[baseId]?.name === columnName) {
			updatedSelectedColumn[baseId] = null;
		}

		setSelectedColumn(updatedSelectedColumn);

		if (
			isScorerEnabled &&
			isScorerOpened &&
			columnName.startsWith(scorerPrefix)
		) {
			setScorerColumns((prev) => prev.filter((col) => col.name !== columnName));
		}
	};

	const handleColumnClick = (column: ScreenerColumn) => {
		if (column.name !== selectedColumn[baseId]?.name) {
			const fullColumn = {
				...column,
				subFields: allSubFields[column.name] || column.subFields,
			};

			setIsRelative(false);

			setSelectedColumn({
				...selectedColumn,
				[baseId]: fullColumn,
			});
		}

		const shouldAddToScorer =
			isScorerEnabled &&
			column.type === "num" &&
			isScorerOpened &&
			!column.name.startsWith(scorerPrefix) &&
			!scorerColumns.some((col) => col.name.includes(column.name)) &&
			scorerColumns.length < 6;

		if (shouldAddToScorer) {
			const newColumn = {
				...column,
				name: `${scorerPrefix}${column.name}`,
				...(column.subFields?.length && {
					subFields: getFirstSubfield(column.subFields),
				}),
			};

			setScorerColumns((prev) => [...prev, newColumn]);
		}
	};

	return (
		<div className="min-w-40 w-2/4 border-box rounded-sm">
			<h3 className="py-1 px-1.5 text-accent-foreground italic">Column set</h3>
			<div
				ref={parentRef}
				className="flex-col items-center gap-2 px-1.5 max-h-base h-full overflow-y-scroll"
			>
				{visibleColumns[baseId].map((column, index) => (
					<div
						key={column.name}
						className="flex-col items-center gap-1 mb-0.5 justify-between"
					>
						<div className="w-full flex items-center justify-between">
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										className="flex justify-between hover:shadow-none h-max 
										p-0.5 text-[var(--light-gray)] hover:text-accent-foreground w-fit"
										onClick={() => {
											handleColumnClick(column);
										}}
										onKeyDown={(e) => {
											if (e.key === KEY.ENTER) {
												handleColumnClick(column);
											}
										}}
									>
										<div>{column?.titleShort ?? column?.title}</div>
									</div>
								</TooltipTrigger>
								<TooltipContent side="right">
									<span>{column.title}</span>
								</TooltipContent>
							</Tooltip>

							<Button
								className="h-max p-0 shadow-none group bg-transparent"
								onClick={() => removeVisibleColumn(column.name)}
							>
								<svg
									aria-label="close"
									role="img"
									fill="none"
									stroke="var(--light-gray)"
									className="w-3 h-3 group-hover:stroke-accent-foreground"
								>
									<use xlinkHref={`${sprites}#close`} />
								</svg>
							</Button>
						</div>

						{allSubFields[column.name]?.length && (
							<Dropdown
								placeholder="Select subfield..."
								options={flattenSubfieldsToDropdownOptions(
									allSubFields[column.name],
								)}
								value={
									column.subFields?.flatMap((subfield) => {
										return flattenSubfieldsToDropdownOptions([subfield]);
									}) || []
								}
								onChange={(selectedOptions) => {
									const selectedSubfields = Array.isArray(selectedOptions)
										? selectedOptions.map((option) => option.value)
										: [];

									updateVisibleColumnsWithSubFields(
										column.name,
										selectedSubfields,
									);

									if (!selectedSubfields.length) {
										removeVisibleColumn(column.name);
									}
								}}
								maxMenuHeight={80}
								isTypeList={true}
								parentRef={parentRef}
								isLast={index === visibleColumns[baseId].length - 1}
								isCondensed={true}
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
